import { useEffect, useState } from "react";
import { api, formatINR } from "../lib/api";
import { Card } from "../components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { useAuth } from "../lib/auth.jsx";
import {
  Wifi,
  Tv,
  Wallet,
  Users as UsersIcon,
  CircleDollarSign,
  TrendingUp,
  PiggyBank,
  TrendingDown,
} from "lucide-react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  Legend,
  CartesianGrid,
} from "recharts";

function StatTile({ icon: Icon, label, value, accent = "red", sub, testid }) {
  return (
    <Card className="p-5" data-testid={testid}>
      <div className="flex items-start justify-between">
        <div>
          <div className="text-xs uppercase tracking-wider text-gray-500">{label}</div>
          <div className="pds-stat-num text-3xl mt-2 text-gray-900">{value}</div>
          {sub && <div className="text-xs text-gray-500 mt-1">{sub}</div>}
        </div>
        <div className={`h-10 w-10 rounded-md grid place-items-center ${accent === "red" ? "bg-red-50 text-red-600" : accent === "green" ? "bg-green-50 text-green-700" : accent === "amber" ? "bg-amber-50 text-amber-700" : "bg-gray-100 text-gray-700"}`}>
          <Icon className="h-5 w-5" />
        </div>
      </div>
    </Card>
  );
}

function MethodRow({ label, internet, cable }) {
  return (
    <div className="flex items-center justify-between py-2 border-b last:border-b-0 border-gray-100">
      <div className="text-sm text-gray-600">{label}</div>
      <div className="flex gap-6 text-sm">
        <div className="w-28 text-right tabular-nums"><span className="text-gray-400">Internet</span> <span className="font-semibold text-gray-900 ml-1">{formatINR(internet)}</span></div>
        <div className="w-28 text-right tabular-nums"><span className="text-gray-400">Cable</span> <span className="font-semibold text-gray-900 ml-1">{formatINR(cable)}</span></div>
      </div>
    </div>
  );
}

function CollectionsBreakdown({ data }) {
  return (
    <>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        <StatTile icon={Wifi} label="Internet" value={formatINR(data.internet.total)} testid={`tile-internet`} />
        <StatTile icon={Tv} label="Cable TV" value={formatINR(data.cable.total)} testid={`tile-cable`} />
        <StatTile icon={Wallet} label="Total" value={formatINR(data.grand_total)} testid={`tile-total`} />
      </div>
      <Card className="mt-4 p-5">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-display font-semibold text-lg">By Payment Method</h3>
          <CircleDollarSign className="h-4 w-4 text-gray-400" />
        </div>
        <MethodRow label="Cash" internet={data.internet.cash} cable={data.cable.cash} />
        <MethodRow label="GPay" internet={data.internet.gpay} cable={data.cable.gpay} />
        <MethodRow label="PhonePe" internet={data.internet.phonepe} cable={data.cable.phonepe} />
      </Card>
    </>
  );
}

export default function DashboardPage() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);

  useEffect(() => {
    api.get("/dashboard/stats").then((r) => setStats(r.data)).catch(() => {});
  }, []);

  if (!stats) {
    return <div className="text-gray-500">Loading dashboard…</div>;
  }

  const monthlyData = (stats.monthly_revenue || []).map((m) => ({
    month: m.month,
    Internet: m.internet,
    Cable: m.cable,
  }));

  const exp = stats.expenses_this_month || { internet: { total: 0 }, cable: { total: 0 } };
  const np = stats.net_profit_this_month || { internet: 0, cable: 0, grand_total: 0 };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl md:text-4xl font-display font-bold tracking-tight">Dashboard</h1>
        <p className="text-gray-500 mt-1">Welcome back, <span className="font-medium text-gray-700">{user?.name}</span>.</p>
      </div>

      {/* Today + This Month */}
      <Tabs defaultValue="today" className="w-full">
        <TabsList className="bg-white border border-gray-200">
          <TabsTrigger value="today" data-testid="tab-today">Today</TabsTrigger>
          {user?.role === "admin" && (
            <TabsTrigger value="month" data-testid="tab-this-month">This Month</TabsTrigger>
          )}
        </TabsList>

        <TabsContent value="today" className="mt-4">
          <CollectionsBreakdown data={stats.today} />
          <Card className="mt-4 p-5">
            <h3 className="font-display font-semibold text-lg mb-3">Collectors — Today</h3>
            {stats.collectors_today.length === 0 ? (
              <div className="text-sm text-gray-500">No collections yet today.</div>
            ) : (
              <div className="divide-y divide-gray-100">
                {stats.collectors_today.map((c, i) => (
                  <div key={i} className="py-2 flex justify-between text-sm">
                    <div className="text-gray-700">{c.collector_name || "Unknown"}</div>
                    <div className="flex gap-4">
                      <div className="text-gray-500">{c.count} payments</div>
                      <div className="font-semibold tabular-nums">{formatINR(c.total)}</div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Card>
        </TabsContent>

        <TabsContent value="month" className="mt-4">
          {user?.role === "admin" && (
            <>
          <CollectionsBreakdown data={stats.this_month} />

          {/* Expenses + Net profit this month */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-4">
            <Card className="p-5" data-testid="expenses-month-card">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-display font-semibold text-lg">Expenses — This Month</h3>
                <PiggyBank className="h-4 w-4 text-gray-400" />
              </div>
              <MethodRow label="Salary" internet={exp.internet.salary} cable={exp.cable.salary} />
              <MethodRow label="Inventory" internet={exp.internet.inventory} cable={exp.cable.inventory} />
              <MethodRow label="Drawings" internet={exp.internet.drawings} cable={exp.cable.drawings} />
              <div className="flex items-center justify-between py-2 mt-2 border-t border-gray-200">
                <div className="text-sm font-semibold text-gray-700">Total</div>
                <div className="flex gap-6 text-sm">
                  <div className="w-28 text-right tabular-nums"><span className="text-gray-400">Internet</span> <span className="font-bold text-red-700 ml-1">{formatINR(exp.internet.total)}</span></div>
                  <div className="w-28 text-right tabular-nums"><span className="text-gray-400">Cable</span> <span className="font-bold text-red-700 ml-1">{formatINR(exp.cable.total)}</span></div>
                </div>
              </div>
            </Card>

            <Card className="p-5" data-testid="net-profit-card">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-display font-semibold text-lg">Net Profit — This Month</h3>
                {np.grand_total >= 0
                  ? <TrendingUp className="h-4 w-4 text-green-600" />
                  : <TrendingDown className="h-4 w-4 text-red-600" />}
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div className="border rounded-md p-3">
                  <div className="text-xs uppercase tracking-wider text-gray-500">Internet</div>
                  <div className={`pds-stat-num text-xl mt-1 ${np.internet >= 0 ? "text-green-700" : "text-red-700"}`}>{formatINR(np.internet)}</div>
                </div>
                <div className="border rounded-md p-3">
                  <div className="text-xs uppercase tracking-wider text-gray-500">Cable</div>
                  <div className={`pds-stat-num text-xl mt-1 ${np.cable >= 0 ? "text-green-700" : "text-red-700"}`}>{formatINR(np.cable)}</div>
                </div>
                <div className="border rounded-md p-3 bg-red-50">
                  <div className="text-xs uppercase tracking-wider text-red-700">Combined</div>
                  <div className={`pds-stat-num text-xl mt-1 ${np.grand_total >= 0 ? "text-green-700" : "text-red-700"}`}>{formatINR(np.grand_total)}</div>
                </div>
              </div>
              <p className="text-xs text-gray-500 mt-3">Net profit = Collections this month − Expenses this month.</p>
            </Card>
          </div>
            </>
          )}
        </TabsContent>
      </Tabs>

      {/* Customers + Monthly chart */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card className="p-5">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-display font-semibold text-lg">Customers</h3>
            <UsersIcon className="h-4 w-4 text-gray-400" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            {["internet", "cable"].map((k) => (
              <div key={k} className="border border-gray-200 rounded-md p-4">
                <div className="text-xs uppercase tracking-wider text-gray-500">{k === "internet" ? "Internet" : "Cable TV"}</div>
                <div className="pds-stat-num text-2xl mt-1">{stats.customers[k].total}</div>
                <div className="mt-2 text-xs text-gray-500 flex justify-between">
                  <span className="text-green-600 font-medium">Paid {stats.customers[k].paid}</span>
                  <span className="text-red-600 font-medium">Pending {stats.customers[k].pending}</span>
                </div>
              </div>
            ))}
          </div>
        </Card>

        <Card className="p-5">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-display font-semibold text-lg">Monthly Revenue</h3>
            <TrendingUp className="h-4 w-4 text-gray-400" />
          </div>
          <div className="h-56">
            {monthlyData.length === 0 ? (
              <div className="text-sm text-gray-500 grid place-items-center h-full">No revenue data yet.</div>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={monthlyData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                  <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="Internet" fill="#dc2626" />
                  <Bar dataKey="Cable" fill="#7f1d1d" />
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        </Card>
      </div>
    </div>
  );
}
